// src/components/admin/SalesCriteriaBar.tsx
//
// Sticky filter ribbon at the top of the Sales dashboard. Edits
// `criteria` + persists to the URL + localStorage via the
// `salesCriteria` lib. Offers a "Clear" button + a brand
// autocomplete (so the operator can type "Chief" instead of
// picking a brand_id).
//
// Teddy (the sales domain tool) writes to the same URL, so when
// the LLM says "show me Jan 2025 on brand Chief" it navigates
// to /admin/sales?brand=Chief&year_from=2025&year_to=2025 and
// this bar re-hydrates.

import { useEffect, useState } from "react";
import { Filter, X, Search } from "lucide-react";
import { authHeaders } from "@/lib/api";
import { useTranslation } from "react-i18next";
import {
  type SalesCriteriaState, type GroupBy, summarizeCriteria,
} from "@/lib/salesCriteria";

interface Props {
  criteria: SalesCriteriaState;
  onChange: (c: SalesCriteriaState) => void;
  onClear: () => void;
  years: { year: number; invoices: number }[];
}

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function SalesCriteriaBar({ criteria, onChange, onClear, years }: Props) {
const { t } = useTranslation();
  const [brandQuery, setBrandQuery] = useState(criteria.brand_name ?? "");
  const [brandMatches, setBrandMatches] = useState<{brand_id:number,name:string}[]>([]);

  useEffect(() => { setBrandQuery(criteria.brand_name ?? ""); }, [criteria.brand_name]);

  // Brand autocomplete: debounce 200ms
  useEffect(() => {
    if (!brandQuery || brandQuery === criteria.brand_name) {
      setBrandMatches([]); return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/admin/sales/brands/search?q=${encodeURIComponent(brandQuery)}&limit=8`,
          { headers: authHeaders(), signal: ctrl.signal });
        if (!r.ok) { setBrandMatches([]); return; }
        const d = await r.json();
        setBrandMatches(d.brands ?? []);
      } catch { setBrandMatches([]); }
    }, 200);
    return () => { ctrl.abort(); clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandQuery]);

  function set<K extends keyof SalesCriteriaState>(key: K, value: SalesCriteriaState[K]) {
    onChange({ ...criteria, [key]: value });
  }
  function pickBrand(b: { brand_id: number; name: string }) {
    onChange({ ...criteria, brand_id: b.brand_id, brand_name: b.name });
    setBrandQuery(b.name);
    setBrandMatches([]);
  }

  const hasAny = !!summarizeCriteria(criteria).match(/brand|month|year|category|customer|product/);

  return (
    <div className="rounded-lg border border-border bg-bg-elevated/60 p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-fg-tertiary">
        <Filter className="h-3.5 w-3.5" />
        <span>Filters</span>
        {hasAny && (
          <span className="ml-auto text-fg-tertiary normal-case tracking-normal">
            Showing {summarizeCriteria(criteria)}
          </span>
        )}
        {hasAny && (
          <button
            type="button"
            onClick={onClear}
            className="rounded p-1 text-fg-tertiary hover:text-fg-primary"
            title={t("admin.salesCriteriaBar.clearAll")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
        {/* Brand picker with autocomplete */}
        <div className="relative col-span-2">
          <label className="mb-1 block text-[10px] text-fg-tertiary">Brand</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-quaternary" />
            <input
              type="text"
              value={brandQuery}
              onChange={(e) => setBrandQuery(e.target.value)}
              placeholder={t("admin.salesCriteriaBar.brandPlaceholder")}
              className="w-full rounded border border-border bg-bg-canvas pl-7 pr-2 py-1.5 text-sm"
            />
          </div>
          {brandMatches.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-border bg-bg-elevated shadow-xl">
              {brandMatches.map((b) => (
                <li key={b.brand_id}>
                  <button
                    type="button"
                    onClick={() => pickBrand(b)}
                    className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-elevated"
                  >
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Year range */}
        <div>
          <label className="mb-1 block text-[10px] text-fg-tertiary">{t("admin.salesCriteriaBar.yearFrom")}</label>
          <select
            className="w-full rounded border border-border bg-bg-canvas px-2 py-1.5 text-sm"
            value={criteria.year_from ?? ""}
            onChange={(e) => set("year_from", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">any</option>
            {years.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] text-fg-tertiary">{t("admin.salesCriteriaBar.yearTo")}</label>
          <select
            className="w-full rounded border border-border bg-bg-canvas px-2 py-1.5 text-sm"
            value={criteria.year_to ?? ""}
            onChange={(e) => set("year_to", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">any</option>
            {years.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}
          </select>
        </div>

        {/* Month range */}
        <div>
          <label className="mb-1 block text-[10px] text-fg-tertiary">{t("admin.salesCriteriaBar.monthFrom")}</label>
          <select
            className="w-full rounded border border-border bg-bg-canvas px-2 py-1.5 text-sm"
            value={criteria.month_from ?? ""}
            onChange={(e) => set("month_from", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {MONTHS.slice(1).map((m, i) =>
              <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] text-fg-tertiary">{t("admin.salesCriteriaBar.monthTo")}</label>
          <select
            className="w-full rounded border border-border bg-bg-canvas px-2 py-1.5 text-sm"
            value={criteria.month_to ?? ""}
            onChange={(e) => set("month_to", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {MONTHS.slice(1).map((m, i) =>
              <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>

        {/* Group by */}
        <div>
          <label className="mb-1 block text-[10px] text-fg-tertiary">{t("admin.salesCriteriaBar.groupBy")}</label>
          <select
            className="w-full rounded border border-border bg-bg-canvas px-2 py-1.5 text-sm"
            value={criteria.group_by}
            onChange={(e) => set("group_by", e.target.value as GroupBy)}
          >
            <option value="brand">Brand</option>
            <option value="category">Category</option>
            <option value="product">Product</option>
          </select>
        </div>
      </div>
    </div>
  );
}
